import type {
  AuditEntryDto,
  CreateRoleRequest,
  CreateUserRequest,
  GrantSupportAccessRequest,
  PageDto,
  PermissionGroupDto,
  RoleDto,
  SupportAccessDto,
  UpdateRoleRequest,
  UpdateUserRequest,
  UserDto,
} from '@an-crm/shared';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api/client';

export const adminKeys = {
  users: ['admin', 'users'] as const,
  roles: ['admin', 'roles'] as const,
  audit: ['admin', 'audit'] as const,
  permissions: ['admin', 'permissions'] as const,
  supportAccess: ['admin', 'support-access'] as const,
};

export const permissionsQuery = queryOptions({
  queryKey: adminKeys.permissions,
  queryFn: ({ signal }) => api<PermissionGroupDto[]>('/admin/permissions', { signal }),
  staleTime: 5 * 60_000,
});

export const supportAccessQuery = queryOptions({
  queryKey: adminKeys.supportAccess,
  queryFn: ({ signal }) => api<SupportAccessDto>('/admin/support-access', { signal }),
});

export const usersQuery = queryOptions({
  queryKey: adminKeys.users,
  queryFn: ({ signal }) => api<UserDto[]>('/admin/users', { signal }),
});

export const rolesQuery = queryOptions({
  queryKey: adminKeys.roles,
  queryFn: ({ signal }) => api<RoleDto[]>('/admin/roles', { signal }),
  staleTime: 5 * 60_000,
});

export const AUDIT_PAGE_SIZE = 50;

export const auditQuery = infiniteQueryOptions({
  queryKey: adminKeys.audit,
  queryFn: ({ pageParam, signal }) =>
    api<PageDto<AuditEntryDto>>('/admin/audit', {
      query: { limit: AUDIT_PAGE_SIZE, cursor: pageParam },
      signal,
    }),
  initialPageParam: null as string | null,
  getNextPageParam: (last) => last.nextCursor,
});

export const adminApi = {
  createUser: (body: CreateUserRequest) => api<UserDto>('/admin/users', { method: 'POST', body }),
  updateUser: (id: string, body: UpdateUserRequest) =>
    api<UserDto>(`/admin/users/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
  unlockUser: (id: string) =>
    api<UserDto>(`/admin/users/${encodeURIComponent(id)}/unlock`, { method: 'POST' }),
  resetMfa: (id: string) =>
    api<UserDto>(`/admin/users/${encodeURIComponent(id)}/reset-mfa`, { method: 'POST' }),
  createRole: (body: CreateRoleRequest) => api<RoleDto>('/admin/roles', { method: 'POST', body }),
  updateRole: (id: string, body: UpdateRoleRequest) =>
    api<RoleDto>(`/admin/roles/${encodeURIComponent(id)}`, { method: 'PATCH', body }),
  deleteRole: (id: string) => api<void>(`/admin/roles/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  grantSupportAccess: (body: GrantSupportAccessRequest) =>
    api<SupportAccessDto>('/admin/support-access', { method: 'POST', body }),
  revokeSupportAccess: () => api<SupportAccessDto>('/admin/support-access', { method: 'DELETE' }),
};
